import { BehaviorSubject } from 'rxjs';

import AmbraContainer from '../components/ambraContainer/AmbraContainer';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ImageWrapper from '../components/common/ImageWrapper';
import VideoWrapper from '../components/common/VideoWrapper';
import ObjViewer from '../components/objViewer/ObjViewer';
import PdfViewer from '../components/pdfViewer/PdfViewer';
import SelectMaterials from '../components/selectMaterials/SelectMaterials';
import { DCM, DOCS, EDUCATIONAL, IMAGE, resourcePrivacy, SUPPLEMENTARY, THREE, VIDEO } from '../models/Resource';
import { debounce, deepCopy } from '../utils/lodashUtils';

export default class TemplateDetailsBloc {
  isLoading$ = new BehaviorSubject(false);
  isTagsLoading$ = new BehaviorSubject(false);
  loadingResourceInfo$ = new BehaviorSubject(null);

  template$ = new BehaviorSubject({
    description: '',
    educationalMaterials: [],
    id: undefined,
    supplementaryMaterials: [],
    tags: [],
    title: '',
  });

  redirectTarget$ = new BehaviorSubject('');

  debounceFn = {
    getDetails: null,
    getIds: null,
    validateToken: null,
  };

  constructor({ templateRepository, errorRepository, resourceRepository, userRepository, authRepository, dialogRepository, appRepository, patientRepository, materialRepository }) {
    this.templateRepository = templateRepository;
    this.errorRepository = errorRepository;
    this.userRepository = userRepository;
    this.authRepository = authRepository;
    this.dialogRepository = dialogRepository;
    this.materialRepository = materialRepository;
    this.appRepository = appRepository;
    this.patientRepository = patientRepository;
    this.resourceRepository = resourceRepository;

    this.debounceFn = {
      getDetails: null,
      getIds: null,
      validateToken: null,
    };

    this.getTags();
  }

  dispose() {
    this.debounceFn = {
      getDetails: null,
      getIds: null,
      validateToken: null,
    };
  }

  get tags$() {
    return this.templateRepository.tags$;
  }

  get userWorkspace$() {
    return this.authRepository.userWorkspace$;
  }

  get userDetails$() {
    return this.userRepository.userDetails$;
  }

  changeTemplate(updated = {}) {
    const values = deepCopy(this.template$.getValue());
    this.template$.next({ ...values, ...updated });
  }

  async processMaterialIds(ids = []) {
    if (this.debounceFn.getIds) {
      this.debounceFn.getIds.cancel();
    }

    this.isLoading$.next(true);
    this.debounceFn.getIds = debounce(async () => {
      if (!ids.length) return;

      const template = deepCopy(this.template$.getValue());
      const responses = [];

      const uniqueIds = Array.from(new Set(ids));

      for (let idx = 0; idx < uniqueIds.length; idx++) {
        responses.push(this.materialRepository.getMaterialDetails(uniqueIds[idx]));
      }

      try {
        this.isLoading$.next(true);
        const materials = await Promise.all(responses);

        const educationalMaterials = materials.filter((m) => m.category === EDUCATIONAL);
        const supplementaryMaterials = materials.filter((m) => m.category === SUPPLEMENTARY);

        if (!template?.educationalMaterials) template.educationalMaterials = [];
        if (!template?.supplementaryMaterials) template.supplementaryMaterials = [];

        template.educationalMaterials = educationalMaterials.map((m, index) => ({ id: m.id, material: m, materialId: m.id, order: index + 1 }));
        template.supplementaryMaterials = supplementaryMaterials.map((m, index) => ({ id: m.id, material: m, materialId: m.id, order: index + 1 }));

        this.template$.next(template);
      } catch (e) {
        this.handleEmitError(e?.message || 'Failed to get materials');
      } finally {
        this.isLoading$.next(false);
      }
    }, 500);

    this.debounceFn.getIds();
  }

  sortMaterialsOrder() {
    const template = this.template$.getValue();
    const educationalMaterials = [...(template.educationalMaterials || [])];
    const supplementaryMaterials = [...(template.supplementaryMaterials || [])];

    let sortedEducationalMaterials = educationalMaterials.sort((a, b) => a.order - b.order);
    let sortedSupplementaryMaterials = supplementaryMaterials.sort((a, b) => a.order - b.order);

    sortedEducationalMaterials = sortedEducationalMaterials.map((m, index) => ({ ...m, order: index + 1 }));
    sortedSupplementaryMaterials = sortedSupplementaryMaterials.map((m, index) => ({ ...m, order: index + 1 }));

    this.template$.next({
      ...template,
      educationalMaterials: sortedEducationalMaterials,
      supplementaryMaterials: sortedSupplementaryMaterials,
    });
  }

  openAddEducationalMaterials(opts = {}) {
    this.dialogRepository.loadDialog(SelectMaterials, {
      defaultCriteria: { category: 'educational', type: '3d,image,dcm,video' },
      onCloseViewer: (options = {}) => {
        this.openAddEducationalMaterials(options);
      },
      onSelectMaterials: (materials) => {
        const educationalMaterials = [];

        (materials || []).forEach((m, index) => {
          const materialContent = m?.material || m;
          educationalMaterials.push({ id: m.id, material: materialContent, materialId: m.id, order: index + educationalMaterials.length });
        });

        this.template$.next({
          ...this.template$.value,
          educationalMaterials,
        });

        this.sortMaterialsOrder();

        this.dialogRepository.toggleDialog(false);
      },
      preSelectedMaterials: opts?.selectedMaterials || [],
    });
  }

  openAddSupplementaryMaterials(opts = {}) {
    this.dialogRepository.loadDialog(SelectMaterials, {
      defaultCriteria: { category: 'supplementary', type: '3d,image,dcm,video' },
      onCloseViewer: (options = {}) => {
        this.openAddSupplementaryMaterials(options);
      },
      onSelectMaterials: (materials) => {
        const supplementaryMaterials = [];

        (materials || []).forEach((m, index) => {
          const materialContent = m?.material || m;
          supplementaryMaterials.push({ id: m.id, material: materialContent, materialId: m.id, order: index + supplementaryMaterials.length });
        });

        this.template$.next({
          ...this.template$.value,
          supplementaryMaterials,
        });

        this.sortMaterialsOrder();

        this.dialogRepository.toggleDialog(false);
      },
      preSelectedMaterials: opts?.selectedMaterials || [],
    });
  }

  getTags() {
    if (this.debounceFn.getTags) {
      this.debounceFn.getTags.cancel();
    }

    this.isTagsLoading$.next(true);
    this.debounceFn.getTags = debounce(async () => {
      try {
        await this.templateRepository.getTags();
      } catch (e) {
        this.handleEmitError(e.message);
      } finally {
        this.isTagsLoading$.next(false);
      }
    }, 500);

    this.debounceFn.getTags();
  }

  getTemplateById(id) {
    if (this.debounceFn.getDetails) {
      this.debounceFn.getDetails.cancel();
    }

    this.isLoading$.next(true);
    this.debounceFn.getDetails = debounce(async () => {
      if (!id) {
        this.isLoading$.next(false);
        this.handleEmitError('Collection not found');
        return;
      }

      try {
        const data = await this.templateRepository.getTemplateById(id);

        if (data?.privacy !== resourcePrivacy.GLOBAL) {
          const userWorkspace = this.userWorkspace$.getValue();
          const userDetails = this.userDetails$.getValue();

          if (data?.privacy === resourcePrivacy.WORKSPACE && data?.workspaceId !== userWorkspace?.id) {
            this.redirectTarget$.next('/collections');
            this.handleEmitError('Collection not found');
            this.isLoading$.next(false);
            return;
          }

          if (data?.privacy === resourcePrivacy.PRIVATE && data?.createdUserId !== userDetails?.id) {
            this.redirectTarget$.next('/collections');
            this.handleEmitError('Collection not found');
            this.isLoading$.next(false);
            return;
          }
        }

        this.template$.next(data);
      } catch (e) {
        this.handleEmitError(e.message);
      } finally {
        this.isLoading$.next(false);
      }
    }, 500);

    this.debounceFn.getDetails();
  }

  async createTemplate(payload) {
    try {
      this.isLoading$.next(true);

      const params = {
        ...payload,
        educationalMaterials: (payload.educationalMaterials || []).map((m) => ({ id: m.materialId, order: m.order })),
        supplementaryMaterials: (payload.supplementaryMaterials || []).map((m) => ({ id: m.materialId, order: m.order })),
      };

      await this.templateRepository.createTemplate(params);

      // FOR DESKTOP APP
      // eslint-disable-next-line no-console
      console.log('create-template:success');
      this.redirectTarget$.next('/collections');
      this.handleEmitSuccess('Collection created successfully');
    } catch (e) {
      this.handleEmitError(e.message);
    } finally {
      this.isLoading$.next(false);
    }
  }

  async updateTemplate(id, payload) {
    try {
      this.isLoading$.next(true);

      const params = {
        ...payload,
        educationalMaterials: (payload.educationalMaterials || []).map((m) => ({ id: m.materialId, order: m.order })),
        supplementaryMaterials: (payload.supplementaryMaterials || []).map((m) => ({ id: m.materialId, order: m.order })),
      };

      await this.templateRepository.updateTemplate(id, params);
      this.handleEmitSuccess('Collection updated successfully');
      this.redirectTarget$.next('/collections');
    } catch (e) {
      this.handleEmitError(e.message);
    } finally {
      this.isLoading$.next(false);
    }
  }

  async deleteTemplateConfirmed(id) {
    try {
      this.isLoading$.next(true);
      await this.templateRepository.deleteTemplate(id);
      this.redirectTarget$.next('/collections');
      this.handleEmitSuccess('Collection deleted successfully');
    } catch (e) {
      this.handleEmitError(e.message);
    } finally {
      this.isLoading$.next(false);
    }
  }

  deleteTemplate(id) {
    if (!id) throw new Error('Collection not found');

    this.dialogRepository.loadDialog(ConfirmDialog, {
      cancelBtnAction: () => {
        this.dialogRepository.toggleDialog(false);
      },
      cancelBtnText: 'Cancel',
      message: 'Are you sure you want to delete this collection?',
      okBtnAction: () => {
        this.deleteTemplateConfirmed(id);
        this.dialogRepository.toggleDialog(false);
      },
      okBtnText: 'Delete',
      title: 'Delete Collection',
    });
  }

  async getResourceByUuid(uuid) {
    try {
      this.loadingResourceInfo$.next(uuid);
      return await this.resourceRepository.getResourceInfo(uuid);
    } finally {
      this.loadingResourceInfo$.next(null);
    }
  }

  async openViewer(material, dialogClasses = null, props) {
    if (!material.isReady) {
      this.errorRepository.error$.next({
        message: 'Failed to open file, the resource is not ready!',
        severity: 'error',
      });
      return;
    }

    if (material.type === IMAGE) {
      this.dialogRepository.loadDialog(ImageWrapper, {
        dialogClasses,
        onClose: () => {
          this.dialogRepository.toggleDialog(false);
        },
        url: material.path,
      });
    }
    if (material.type === VIDEO) {
      const videoUrl = material?.h264Path || material.path;
      this.dialogRepository.loadDialog(VideoWrapper, { isPlayOnClick: true, url: videoUrl });
    }

    if (material.type === THREE) {
      this.dialogRepository.loadDialog(ObjViewer, { url: material.path });
    }

    if (material.type === DCM) {
      try {
        this.appRepository.handleOpenLoading();

        // Should reload the resource info because the viewerLink may have expired
        const resourceInfo = await this.getResourceByUuid(material.resource.uuid);

        let childResources = [];
        if (resourceInfo?.id) {
          childResources = await this.patientRepository.getChildResourcesLink(resourceInfo.id);
        }

        const title = resourceInfo?.description || '';

        this.dialogRepository.loadDialog(AmbraContainer, {
          childResources,
          onClose: () => {
            this.dialogRepository.toggleDialog(false);
            if (props?.onClose) props.onClose();
          },
          title,
          url: resourceInfo?.viewerLink,
        });
      } finally {
        this.appRepository.handleCloseLoading();
      }
    }

    if (material.type === DOCS) {
      this.dialogRepository.loadDialog(PdfViewer, { title: material?.title || '', url: material?.resource?.path });
    }
  }

  handleEmitError(message) {
    this.errorRepository.error$.next({
      message,
      severity: 'error',
    });
  }

  handleEmitSuccess(message) {
    this.errorRepository.error$.next({
      message,
      severity: 'success',
    });
  }
}
